/**
 * Utopia Must Dance - Design Constants
 *
 * Central repository for all visual design values including colors, typography,
 * spacing, and other design tokens. This ensures consistency across the entire
 * game and makes it easy to maintain the vector graphics arcade aesthetic.
 *
 * ## Usage Examples
 *
 * ### Colors
 * ```typescript
 * import { Colors } from '../config/DesignConstants';
 *
 * this.sprite.stroke({ color: Colors.RED, width: 2 });
 * this.sprite.fill({ color: Colors.DARK_BLUE });
 * ```
 *
 * ### Typography
 * ```typescript
 * import { Typography } from '../config/DesignConstants';
 *
 * new VectorText({
 *   text: "GAME OVER",
 *   scale: Typography.FONT_SIZE.LARGE_HEADER / 10,
 *   color: Colors.RED
 * });
 * ```
 *
 * ### Spacing
 * ```typescript
 * import { Spacing } from '../config/DesignConstants';
 *
 * this.width = textWidth + Spacing.XL;
 * this.height = textHeight + Spacing.MD;
 * ```
 *
 * ### Line Art
 * ```typescript
 * import { LineArt } from '../config/DesignConstants';
 *
 * this.sprite.stroke({
 *   color: Colors.CYAN,
 *   width: LineArt.LINE_WIDTH.STANDARD
 * });
 * ```
 */

// =============================================================================
// COLOR PALETTE
// =============================================================================

/** Color palette for the vector graphics aesthetic */
export const Colors = {
  WHITE: 0xffffff,
  GRAY: 0xaaaaaa,
  BLACK: 0x000010, // Not actually black, but I like the dark blue more

  RED: 0xff0000,
  DARK_RED: 0xaa0000,
  CRIMSON: 0xdc143c,
  DARK_CRIMSON: 0x8b0000,
  CORAL: 0xff6347,
  DARK_CORAL: 0x8b3626,
  ORANGE: 0xff4500,
  DARK_ORANGE: 0xaa3000,
  YELLOW: 0xffff00,
  DARK_YELLOW: 0xaaaa00,
  GREEN: 0x00ff00,
  DARK_GREEN: 0x00aa00,
  CYAN: 0x00ffff,
  DARK_CYAN: 0x00aaaa,
  BLUE: 0x0080ff,
  DARK_BLUE: 0x005599,
  PINK: 0xff1493,
  DARK_PINK: 0xaa0e62,
  MAGENTA: 0xff00ff,
  DARK_MAGENTA: 0x800080,
  PURPLE: 0x8a2be2,
  DARK_PURPLE: 0x5c1d96,
} as const;

// =============================================================================
// TYPOGRAPHY
// =============================================================================

/** Typography constants for vector text */
export const Typography = {
  // Font sizes (in pixels)
  FONT_SIZE: {
    LARGE_HEADER: 32, // Game title, major screens
    MEDIUM_HEADER: 24, // Section headers, level indicators
    UI_TEXT: 16, // Buttons, scores, status
    SMALL_TEXT: 12, // Fine details, metadata

    // Legacy sizes (for gradual migration)
    TITLE_LARGE: 64, // 4rem equivalent
    TITLE_MEDIUM: 40, // 2.5rem equivalent
    TITLE_STANDARD: 48, // 3rem equivalent
    BUTTON_TEXT: 24, // 1.5rem equivalent
    BODY_TEXT: 19, // 1.2rem equivalent
    SCORE_LARGE: 24, // Score display
    SCORE_SMALL: 20, // Enemies remaining
  },
} as const;

// =============================================================================
// SPACING & LAYOUT
// =============================================================================

/** Consistent spacing scale */
export const Spacing = {
  XS: 4, // 0.25rem
  SM: 8, // 0.5rem
  MD: 16, // 1rem
  LG: 24, // 1.5rem
  XL: 32, // 2rem
  XXL: 48, // 3rem

  // Common layout values
  PADDING: {
    BUTTON: 16, // 1rem
    BUTTON_LARGE: 24, // 1.5rem
    CARD: 24, // 1.5rem
    SCREEN_EDGE: 20, // 20px from edges
  },

  MARGIN: {
    BUTTON_GROUP: 16, // Gap between buttons
    SECTION: 32, // Between major sections
    TITLE: 32, // Below titles
  },
} as const;

// =============================================================================
// LINE ART & GRAPHICS
// =============================================================================

/** Vector graphics styling constants */
export const LineArt = {
  // Line weights
  LINE_WIDTH: {
    THIN: 1, // Fine details
    STANDARD: 2, // Most elements
    HEAVY: 3, // Emphasis elements
  },

  // Corner styles
  LINE_JOIN: "round" as const,
  LINE_CAP: "round" as const,
} as const;
